import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';

/**
 * Product-facing dashboard error card.
 *
 * Classification is driven by HTTP `status` when the caller has it
 * (from `PortalApiError`). The previous `message.includes('owner')`
 * heuristic was DELETED: it false-positived on the URL path substring
 * "owner" in any `/api/owner/*` error, so a plain 404 on
 * `/api/owner/analytics` rendered "Access restricted". Access is now
 * decided ONLY by status 403 — never by substring-matching the message.
 *
 * `status` is optional: callers that still pass only a `message` (no
 * status) fall back to the genuine auth-text detection or the generic
 * error display — never to an access-restricted guess.
 *
 * STYLING (SHARED-PRIMITIVES-CONFORMANCE-V1; audit #603 TOP FIVE item 2):
 * re-tokened IN PLACE onto the canonical vocabulary -- the shared
 * `.ac-banner` card (`--calm` for the quiet status cards; the danger
 * palette tokens for the generic fallback, NOT `--danger`, whose 4px
 * accent bar would change the card's composition) plus the `--ac-*`
 * scales for the inner type/spacing. The banner vocabulary registers no
 * child classes and this package mints none (NO-NEW-CLASS fence), so the
 * title/body/link lines ride the token scales inline. Same component,
 * same registry entry (component.dashboard-load-error), same props, same
 * copy, same classification; ErrorState stays the separate IN-FLOW
 * section wrapper (its COMPETITOR FENCE).
 */

/** Card title line: ink over the banner's muted base. */
const TITLE_STYLE: CSSProperties = {
  margin: 0,
  fontWeight: 500,
  color: 'hsl(var(--ac-ink))',
};

/** Card body line: muted ink, one space-1 step under the title. */
const BODY_STYLE: CSSProperties = {
  margin: 'var(--ac-space-1) 0 0',
  color: 'hsl(var(--ac-ink-muted))',
};

/** The sign-in affordance: brand-coloured text link on the card. */
const LINK_STYLE: CSSProperties = {
  display: 'inline-block',
  marginTop: 'var(--ac-space-2)',
  fontWeight: 500,
  color: 'hsl(var(--ac-brand))',
};

/** Generic-fallback palette: danger tokens on the banner shape. */
const DANGER_STYLE: CSSProperties = {
  borderColor: 'hsl(var(--ac-danger) / 0.4)',
  background: 'hsl(var(--ac-danger-soft))',
  color: 'hsl(var(--ac-danger-fg))',
};

export default function DashboardLoadError({
  message,
  status,
  code,
}: {
  message: string;
  status?: number;
  /** Structured backend reason code (PortalApiError.code) when the caller has
   *  it. `NO_PROFILE` = authenticated but not provisioned (distinct from a
   *  signed-out 401). Optional -- absent falls back to message/status. */
  code?: string;
}): JSX.Element {
  const lower = message.toLowerCase();
  // Authenticated but NOT provisioned: a valid identity with no staff profile.
  // Sign-in is the WRONG CTA here -- the fix is provisioning, not re-login.
  // Driven by the NO_PROFILE reason code when present, else the backend's
  // "no staff profile" auth text (which existing callers already pass through
  // `message`, so no call-site change is needed to fix that case).
  const notProvisioned =
    code === 'NO_PROFILE' || lower.includes('no staff profile');

  // 404 — the endpoint isn't available (typically a deploy gap or a
  // feature still rolling out). An honest "not available yet" card,
  // not an error and not an access message.
  if (status === 404) {
    return (
      <div className="ac-banner ac-banner--calm" role="status">
        <p style={TITLE_STYLE}>Not available yet</p>
        <p style={BODY_STYLE}>
          This section is still being rolled out. Please check back shortly.
        </p>
      </div>
    );
  }

  // Authenticated-but-unresolved (checked BEFORE the 401 sign-in card so a
  // NO_PROFILE 401 never misleadingly tells a signed-in user to sign in).
  if (notProvisioned) {
    return (
      <div className="ac-banner ac-banner--calm" role="status">
        <p style={TITLE_STYLE}>Account not set up for the dashboard</p>
        <p style={BODY_STYLE}>
          You are signed in, but this account does not have dashboard access
          yet. Ask your administrator to provision your staff role, then reload.
        </p>
      </div>
    );
  }

  // 401 — unauthenticated. Prompt sign-in.
  if (status === 401) {
    return (
      <div className="ac-banner ac-banner--calm">
        <p style={TITLE_STYLE}>Sign in to continue</p>
        <p style={BODY_STYLE}>
          Use your organization&apos;s Google or Microsoft account on the login page.
        </p>
        <Link to="/login" style={LINK_STYLE}>
          Sign in
        </Link>
      </div>
    );
  }

  // 403 — authenticated but forbidden. Access decided by STATUS, not by
  // any substring in the message.
  if (status === 403) {
    return (
      <div className="ac-banner ac-banner--calm">
        <p style={TITLE_STYLE}>Access restricted</p>
        <p style={BODY_STYLE}>
          You don&apos;t have access to this area. Ask your administrator if you need it.
        </p>
      </div>
    );
  }

  // Message-only fallback (no status): genuine auth-error TEXT still maps
  // to the sign-in card. These tokens come from the backend auth layer,
  // not from URL paths, so they carry no path-substring false-positive
  // risk. This is NOT the deleted owner-path heuristic.
  // 'no staff profile' is handled by `notProvisioned` above (the not-set-up
  // card), so it is intentionally NOT in this signed-out list.
  const authIssue =
    lower.includes('no authorization') ||
    lower.includes('not authenticated') ||
    lower.includes('invalid token');

  if (authIssue) {
    return (
      <div className="ac-banner ac-banner--calm">
        <p style={TITLE_STYLE}>Sign in to continue</p>
        <p style={BODY_STYLE}>
          Use your organization&apos;s Google or Microsoft account on the login page.
        </p>
        <Link to="/login" style={LINK_STYLE}>
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="ac-banner" role="alert" style={DANGER_STYLE}>
      {message}
    </div>
  );
}
